import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle } from "lucide-react";

const products = [
  {
    id: "school-management",
    title: "School / Learning Management System",
    description:
      "Streamline administrative tasks for educational institutions. Our SMS manages student data, records, and finance with total transparency.",
    details: [
      "Student Information Management",
      "Automated Fee Collection & Invoicing",
      "Examination & Result Processing",
      "Parent-Teacher Communication Portal",
    ],
    img: "/products/schoolmag.png",
  },
  {
    id: "hotel-management",
    title: "Hotel Management System",
    description:
      "Optimize day-to-day hospitality operations. From front desk tasks to revenue reporting, our HMS delivers exceptional guest service efficiency.",
    details: [
      "Real-time Room Booking Engine",
      "Housekeeping & Staff Management",
      "POS Integration for Restaurants",
      "Night Audit & Financial Reporting",
    ],
    img: "/products/hotelm.jpg",
  },
  {
    id: "clinic-management",
    title: "Clinic Management System",
    description:
      "Automate patient appointments, billing, and clinical operations. Designed to help healthcare providers focus on what matters: patient care.",
    details: [
      "Electronic Medical Records (EMR)",
      "Doctor's Schedule Management",
      "Pharmacy & Inventory Control",
      "Insurance Claim Integration",
    ],
    img: "/products/clientmg.png",
  },
  {
    id: "erp-crm",
    title: "ERP & CRM Systems",
    description:
      "Consolidate customer info, manage contacts, and track sales. Features include HR modules, billing, and project management with robust reporting.",
    details: [
      "Lead & Opportunity Tracking",
      "Supply Chain Management",
      "HR & Payroll Automation",
      "Dynamic Dashboard Analytics",
    ],
    img: "/products/crmerp.jpg",
  },
  {
    id: "cloud-pbx",
    title: "Cloud PBX",
    description:
      "A virtual phone system hosted in the cloud, allowing businesses to manage communication needs over the internet without traditional hardware. Perfect for remote or distributed teams.",
    details: [
      "Scalable & Cost-effective Solution",
      "Internal & External Call Management",
      "Remote Team Accessibility",
      "Hardware-free Setup",
    ],
    img: "/products/cloudpbx.png",
  },
  {
    id: "sms-gateway",
    title: "SMS Gateway",
    description:
      "A bridge between messaging platforms that enables sending and receiving messages across different Mobile Operators like ATOM, Ooredoo, MPT, and Mytel.",
    details: [
      "Marketing, Notifications & Auth",
      "Sender ID Customization",
      "Message Queuing & Delivery Reports",
      "Two-way Messaging & Security",
    ],
    img: "/products/smsgate.png",
  },
  {
    id: "payment-gateway",
    title: "Payment Gateway",
    description:
      "Securely connects merchant websites to payment processors or banks. It encrypts transaction info, supporting methods like KBZPay, WavePay, Visa, and MPU.",
    details: [
      "Secure Online Transactions",
      "MMQR & Local Bank Integration",
      "International Card Support (Visa/JCB)",
      "Encrypted Data Compliance",
    ],
    img: "/products/paymentgate.png",
  },
  {
    id: "marketing-tools",
    title: "Marketing Tools",
    description:
      "Powerful platforms designed to help businesses connect with audiences efficiently through automated SMS and email communication channels.",
    details: [
      "Campaign Scheduling & Management",
      "Transactional Message Automation",
      "Multi-channel (SMS & Email)",
      "Audience Engagement Analytics",
    ],
    img: "/products/marketingtools.png",
  },
  {
    id: "top-up-gateway",
    title: "Top-up Gateway",
    description:
      "A dedicated API-driven service designed to process mobile airtime recharges and data pack purchases for all major telecommunication operators in Myanmar, including ATOM, Ooredoo, MPT, and Mytel.",
    details: [
      "Real-time Mobile Airtime Recharges",
      "Data Pack Purchase Processing",
      "Supports E-Load & E-Pin Methods",
      "Uninterrupted API-driven Integration",
    ],
    img: "/services/topupgate.jpg",
  },
];

export default function ProductDetail() {
  const { id } = useParams();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Search in products only
  const item = products.find((item) => item.id === id);

  if (!item) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Product Not Found</h1>
          <Link to="/products" className="text-cyan-400 hover:underline">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans pb-32">
      {/* Hero Section */}
      <section className="relative min-h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden pt-20 md:pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 max-w-5xl">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6 md:mb-8">
            <ArrowLeft size={20} />
            Back to Products
          </Link>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] md:leading-[0.85] mb-4 md:mb-6">
            {item.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-3xl mx-auto text-gray-400 text-sm md:text-lg lg:text-xl font-medium leading-relaxed px-2">
            {item.description}
          </motion.p>
        </div>
      </section>

      {/* Details Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
          {/* Image */}
          {item.img && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative order-2 md:order-1">
              <div className="absolute -inset-2 md:-inset-4 bg-gradient-to-r from-blue-600 to-emerald-400 rounded-2xl md:rounded-3xl blur-xl opacity-10"></div>
              <div className="relative bg-[#1A202C] rounded-2xl md:rounded-3xl border border-white/5 p-6 md:p-12 flex items-center justify-center">
                <img
                  src={item.img}
                  alt={item.title}
                  className="max-w-full max-h-[250px] rounded-2xl md:max-h-[400px] object-contain drop-shadow-2xl"
                />
              </div>
            </motion.div>
          )}

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6 md:space-y-8 order-1 md:order-2">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter mb-4 md:mb-6">
                Core Features
              </h2>
              <div className="space-y-3 md:space-y-4">
                {item.details.map((detail, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-start gap-3 bg-[#161B22]/50 border border-white/10 p-4 rounded-xl hover:border-[#00BCF0]/30 transition-all">
                    <CheckCircle className="w-5 h-5 text-[#00BCF0] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm md:text-base leading-relaxed">
                      {detail}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pt-4 flex gap-4">
              <Link
                to="/contact#contact-form"
                state={{
                  source: "product",
                  productTitle: item.title,
                }}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                Request Demo
              </Link>
              {/* <Link
                to="/products"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#00BCF0] to-[#007BFF] rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                Learn More
              </Link> */}
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
